import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Comment } from '@shared/models/icommons.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { CommentsApiService } from './comments-api.service';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

const EVERY_30_SECONDS = 30000;

@UntilDestroy()
@Component({
  selector: 'miq-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
  standalone: false,
})
export class CommentsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title: string;
  @Input() type: string;
  @Input() set conversationId(id: string | number) {
    if (id != null && `${id}`.trim() !== '') {
      this._conversationId = `${id}`;
    }
  }
  private _conversationId: string;

  private submitRequested = new EventEmitter<string>();

  comments: Comment[];
  currentUserEmail: any;

  messageGroup: UntypedFormGroup;

  loading: boolean;

  timer$: Subscription;

  constructor(
    private userResolver: UserInfoResolver,
    private commentsApi: CommentsApiService,
    private fb: UntypedFormBuilder,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    const { emailAddress } = this.userResolver.resolve();
    this.currentUserEmail = emailAddress;
    this.messageGroup = this.fb.group({ message: [] });
    this.loading = true;
    this.timer$ = timer(EVERY_30_SECONDS).pipe(untilDestroyed(this)).subscribe(this.fetchComments.bind(this));

    this.submitRequested
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        untilDestroyed(this),
        switchMap(message => this.commentsApi.create(this.type, this._conversationId, { message }))
      )
      .subscribe({ next: this.onMessageSent.bind(this), error: this.onMessageSentFailure.bind(this) });
  }

  ngOnInit() {
    /* required for static lable translation */
  }

  ngOnChanges() {
    if (this._conversationId && this.type) {
      this.fetchComments();
    }
  }

  ngOnDestroy() {
    /** Required by untilDestroy */
  }

  fetchComments() {
    return this.commentsApi
      .getConversation(this.type, this._conversationId)
      .pipe(map(this.myComments))
      .subscribe(this.#onFetchComments);
  }

  /**
   * Maps each comment it's belonging
   * @param comments
   * @returns
   */
  myComments = (comments: Comment[]) => comments.map(this.#mine);

  /**
   * Maps comment's belonging
   * @param comment
   * @returns
   */
  #mine = (comment: Comment) => ({ ...comment, itsMe: this.currentUserEmail === comment.email });

  /**
   * Sets the comments to class variable comments and turns loading flag off
   * @param comments
   */
  #onFetchComments = (comments: Comment[]) => {
    this.comments = comments;
    this.loading = false;
  };

  sendMessage($event) {
    $event.preventDefault();
    $event.stopPropagation();
    const { message } = this.messageGroup.value;
    if (message.trim()) {
      this.submitRequested.emit(message.trim());
    }
  }

  get messageControl() {
    return this.messageGroup.controls.message;
  }

  onMessageSent() {
    this.messageControl.setValue('');
    this.fetchComments();
  }

  onMessageSentFailure(error) {
    this.logger.error(error);
  }
}
